import { useEffect, useState } from 'react';
import {
  X,
  MapPin,
  Building2,
  Globe,
  Package,
  FileText,
  User,
  Navigation,
  ExternalLink,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { formatCurrency, formatDateTime } from '@/utils/format';
import { ORDER_STATUS_LABELS } from '@/features/orders/orders.types';
import type { Order, ManualChange } from '@/features/orders/orders.types';
import { EvidenceThumbnail } from './components/evidence-thumbnail';

// ── Types & Helpers ──────────────────────────────────────────────────────────

interface OrderDrawerProps {
  order: Order | null;
  onClose: () => void;
}

type TimelineEventType = 'milestone' | 'manual';

interface TimelineEvent {
  label: string;
  date: string;
  type: TimelineEventType;
  reason?: string | null;
  responsible?: string;
  evidence_path?: string | null;
}

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

function Section(props: SectionProps) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-xs font-bold uppercase tracking-wider text-base-content/40">
        {props.title}
      </h3>
      <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4">
        {props.children}
      </div>
    </div>
  );
}

interface DataRowProps {
  label: string;
  value: string | number | null | undefined;
  icon?: React.ReactNode;
  col_span_full?: boolean;
}

function DataRow(props: DataRowProps) {
  return (
    <div className={twMerge('flex flex-col gap-1', props.col_span_full && 'sm:col-span-2')}>
      <div className="flex items-center gap-1.5 text-xs text-base-content/50">
        {props.icon && <span className="shrink-0">{props.icon}</span>}
        {props.label}
      </div>
      <span className="text-sm font-medium break-words leading-tight">
        {props.value || '—'}
      </span>
    </div>
  );
}

/**
 * Returns a human readable name for a responsible admin.
 */
function formatResponsible(responsible: ManualChange['responsible'] | undefined): string {
  if (!responsible) return '—';
  const { name, email } = responsible;
  if (name && name.trim() !== '') return name;
  if (email && email.trim() !== '') return email;
  return '—';
}

// ── Main Component ────────────────────────────────────────────────────────────

const STATUS_BADGE_CLASS: Record<number, string> = {
  1: 'badge-ghost',
  2: 'badge-info',
  3: 'badge-warning',
  4: 'badge-success',
  5: 'badge-primary',
  6: 'badge-success',
  7: 'badge-error',
  8: 'badge-error',
  10: 'badge-error',
  11: 'badge-warning',
  12: 'badge-warning',
  13: 'badge-success',
};

const EVENT_LABELS: Record<string, string> = {
  delete: 'Eliminación manual',
  deleted: 'Eliminada',
  cancel: 'Cancelación manual',
  CANCELLED: 'Cancelación',
  DELETED: 'Eliminación',
  CREATED: 'Creación',
  STATUS_CHANGE: 'Cambio de estado',
};

export function OrderDrawer(props: OrderDrawerProps) {
  const [is_visible, setIsVisible] = useState(false);
  const { order, onClose } = props;

  useEffect(() => {
    if (order) {
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [order]);

  if (!order) return null;

  const display_id = order.tracking_number || order.external_reference || order.id;

  // 1. Build a unified timeline combining manual changes and lifecycle milestones
  const unified_timeline: TimelineEvent[] = [
    // Lifecycle milestones
    { label: 'Orden creada', date: order.created_at, type: 'milestone' as const },
    ...(order.collected_at ? [{ label: 'Recolectada en punto', date: order.collected_at, type: 'milestone' as const }] : []),
    ...(order.delivered_at ? [{ label: 'Entregada', date: order.delivered_at, type: 'milestone' as const }] : []),

    // Manual changes
    ...(order.manual_changes || []).map((change) => ({
      label: EVENT_LABELS[change.event_name] || change.event_name,
      date: change.changed_at,
      type: 'manual' as const,
      reason: change.reason,
      responsible: formatResponsible(change.responsible),
      evidence_path: change.evidence_gcs_path,
    })),
  ]
    .filter((event) => !!event.date)
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()); // Descending (newest first)

  const formatAddress = (addr: any) => {
    if (!addr || typeof addr !== 'object') return null;
    const parts = [
      addr.street,
      addr.number,
      addr.neighborhood,
      addr.city,
      addr.state,
      addr.postal_code,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
  };

  return (
    <>
      <div
        className={twMerge(
          'fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] transition-opacity duration-300',
          is_visible ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      <div
        role="dialog"
        aria-modal="true"
        className={twMerge(
          'fixed inset-y-0 right-0 z-50 flex w-[500px] max-w-full flex-col bg-base-100 shadow-2xl transition-transform duration-300',
          is_visible ? 'translate-x-0' : 'translate-x-full'
        )}
        style={{ top: 0, height: '100vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-base-300 px-6 py-4 shrink-0">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-black tracking-tight truncate font-mono">
                {display_id}
              </h2>
              <span className={twMerge('badge badge-sm', order.deleted ? 'badge-error' : (STATUS_BADGE_CLASS[order.status_id] || 'badge-ghost'))}>
                {order.deleted ? 'Eliminada' : (ORDER_STATUS_LABELS[order.status_id] || order.status)}
              </span>
            </div>
            <p className="text-xs text-base-content/50 flex items-center gap-1">
              <MapPin size={10} />
              {order.dispatch_place_name}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-8 flex flex-col gap-10">

          {/* 0. Auditoría de Eliminación (Audit Log) */}
          {order.deleted && (
            <div className="alert alert-error bg-error/10 border-error/20 flex flex-col items-start gap-3 text-left">
              <div className="flex items-center gap-2 font-bold text-error">
                <Trash2 size={18} />
                ORDEN ELIMINADA
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full">
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase font-bold opacity-60">Motivo</span>
                  <span className="text-sm font-medium">{order.deleted_reason || 'Sin motivo registrado'}</span>
                </div>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] uppercase font-bold opacity-60">Fecha</span>
                  <span className="text-sm font-medium">{formatDateTime(order.deleted_at || order.updated_at)}</span>
                </div>
              </div>
              {order.manual_changes && order.manual_changes.length > 0 && (
                <div className="flex flex-col gap-1 mt-1 pt-2 border-t border-error/10 w-full">
                  <span className="text-[10px] uppercase font-bold opacity-60">Responsable</span>
                  <span className="text-sm font-medium">
                    {formatResponsible(order.manual_changes[order.manual_changes.length - 1].responsible)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* 1. Datos Generales */}
          <Section title="Datos generales">
            <DataRow label="Cliente" value={order.customer_name} icon={<User size={12} />} />
            <DataRow label="Tipo de orden" value={order.order_type} icon={<Building2 size={12} />} />
            <DataRow label="País" value={order.country_code.toUpperCase()} icon={<Globe size={12} />} />
            <DataRow label="Paquetes" value={order.package_count} icon={<Package size={12} />} />
            <DataRow
              label="Monto"
              value={formatCurrency(parseFloat(order.amount || '0'), order.currency)}
              icon={<FileText size={12} />}
            />
            <DataRow label="Pago" value={order.payment_type} icon={<ShieldCheck size={12} />} />
            <DataRow label="Referencia externa" value={order.external_reference} icon={<ExternalLink size={12} />} col_span_full />
            <DataRow label="Código verificación" value={order.verification_code} icon={<ShieldCheck size={12} />} />
          </Section>

          <div className="divider my-0 opacity-50" />

          {/* 2. Origen / Destino */}
          <Section title="Origen (Remitente)">
            <DataRow label="Nombre" value={(order.shipper as any)?.name} />
            <DataRow label="Teléfono" value={(order.shipper as any)?.phone} />
            <DataRow label="Correo" value={(order.shipper as any)?.email} col_span_full />
            <div className="col-span-full">
              <DataRow
                label="Dirección"
                value={formatAddress((order.shipper as any)?.address)}
                icon={<Navigation size={12} />}
              />
            </div>
          </Section>

          <div className="divider my-0 opacity-50" />

          <Section title="Destino (Destinatario)">
            <DataRow label="Nombre" value={(order.client as any)?.name} />
            <DataRow label="Teléfono" value={(order.client as any)?.phone} />
            <DataRow label="Correo" value={(order.client as any)?.email} col_span_full />
            <div className="col-span-full">
              <DataRow
                label="Dirección"
                value={formatAddress((order.client as any)?.address)}
                icon={<Navigation size={12} />}
              />
            </div>
          </Section>

          <div className="divider my-0 opacity-50" />

          {/* 4. Timeline Unificado */}
          <div className="flex flex-col gap-6">
            <h3 className="text-xs font-bold uppercase tracking-wider text-base-content/40">
              Historial de movimientos
            </h3>
            <div className="flex flex-col gap-0 ml-2 border-l-2 border-base-300 pl-6 relative">
              {unified_timeline.map((event) => (
                <div
                  key={`${event.type}-${event.date}-${event.label}`}
                  className="pb-8 last:pb-0 relative"
                >
                  {/* Dot */}
                  <div className={twMerge(
                    "absolute -left-[31px] top-1 h-3 w-3 rounded-full ring-4 ring-base-100",
                    event.type === 'manual' ? "bg-error" : "bg-primary"
                  )} />

                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-bold leading-none">{event.label}</span>
                      <span className="text-[10px] font-medium opacity-50 whitespace-nowrap bg-base-200 px-1.5 py-0.5 rounded">
                        {formatDateTime(event.date)}
                      </span>
                    </div>

                    {event.responsible && (
                      <div className="flex items-center gap-1.5 text-xs text-base-content/60">
                        <User size={10} className="shrink-0" />
                        <span>Por: {event.responsible}</span>
                      </div>
                    )}

                    {event.reason && (
                      <div className="rounded-lg bg-base-200/50 p-2 text-xs italic text-base-content/70 border border-base-300">
                        "{event.reason}"
                      </div>
                    )}

                    {event.evidence_path && (
                      <div className="mt-1">
                        <EvidenceThumbnail gcs_path={event.evidence_path} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </>
  );
}
